import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
    IconButton,
    Divider,
    FormControlLabel,
    Checkbox,
    Link,
    Select,
    Tooltip,
    Chip,
    Snackbar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import axios from 'axios';
import { Gor } from '../types';
import { API_ENDPOINTS } from '../config/api';
// Import API config to set axios defaults
// dummy comment
import '../config/api';
import { hasRankedMatch } from '../config/rankedMatchGor';
import { Helmet } from 'react-helmet-async';
import { fetchAdminAccessData, isSuperAdminEmail, isAdminOrSuperAdmin } from '../config/adminEmails';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import RefreshIcon from '@mui/icons-material/Refresh';
import { renderAddressWithMapLink } from '../utils/addressUtils';
import { buildShareText } from '../utils/shareTextUtils';
import { calculateBookingPrice } from '../utils/bookingPricing';

export {}; 



type CalendarEvent = {
    title: string;
    description: string;
    location: string;
    start: Date;
    end: Date;
};

interface BookingInfo {
    tanggal: string | null;
    jumlahPemain: number;
    jumlahBookingJadwal?: number;
    akumulasiDiskon?: number;
    daftarPemain: string[];
    pemainByLevel: { [key: string]: string[] };
    kapasitasMaksimal?: number;
    paidNames?: string[];
    paidEmails?: string[]; // Email yang sudah bayar
    pemainEmailMap?: { [key: string]: string }; // Added for new logic
    daftarPemainDetail?: { nama: string; level: string; email: string; idBooking?: string; statusBayar?: boolean; statusBayarText?: string; bayarDiLapangan?: boolean }[]; // Include ID Booking dan status bayar
    daftarPemainBayarDiLapangan?: string[];
    daftarPemainBayarDiLapanganDetail?: { nama: string; level: string; email: string; idBooking?: string; statusBayar?: boolean; statusBayarText?: string; bayarDiLapangan?: boolean }[];
    daftarWaitinglist?: string[];
    waitinglistByLevel?: { [key: string]: string[] };
    daftarWaitinglistDetail?: { nama: string; level: string; email: string; idBooking?: string; statusBayar?: boolean; statusBayarText?: string; bayarDiLapangan?: boolean }[]; // Include ID Booking dan status bayar
    jumlahWaitinglist?: number;
}

function getHariFromTanggal(tanggal: string) {
  if (!tanggal) return '';
  const [tgl, bulan, tahun] = tanggal.split(' ');
  const bulanMap: { [key: string]: number } = {
    'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
    'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
  };
  try {
    const bulanIdx = bulanMap[bulan as keyof typeof bulanMap] ?? -1;
    if (bulanIdx === -1) return '';
    const date = new Date(parseInt(tahun), bulanIdx, parseInt(tgl));
    return date.toLocaleDateString('id-ID', { weekday: 'long' });
  } catch {
    return '';
  }
}

const GorDetail: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [gor, setGor] = useState<Gor | null>(location.state?.gor || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [isAdminUser, setIsAdminUser] = useState<boolean>(user?.email ? isAdminOrSuperAdmin(user.email) : false);
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(user?.email ? isSuperAdminEmail(user.email) : false);
    const lastBooking = localStorage.getItem('lastBooking');
    const defaultNama = lastBooking ? JSON.parse(lastBooking).nama : localStorage.getItem('nama') || '';
    const defaultLevel = lastBooking ? JSON.parse(lastBooking).level : localStorage.getItem('levelBermain') || 'Beginner';
    const defaultNomorHP = lastBooking ? JSON.parse(lastBooking).nomorHP : localStorage.getItem('nomorHP') || '';
    const [formData, setFormData] = useState({
        tanggal: '',
        nama: defaultNama,
        levelBermain: defaultLevel,
        email: user?.email || '',
        nomorHP: defaultNomorHP
    });
    const [useMemberCode, setUseMemberCode] = useState(false);
    const [memberCode, setMemberCode] = useState('');
    const [memberCodeError, setMemberCodeError] = useState('');
    const [memberInfo, setMemberInfo] = useState<Array<{
        status: string;
        memberCode: string;
        activatedAt: string;
        remainingVisits: string;
        expiryDate: string;
    }>>([]);
    const [memberStatusLoading, setMemberStatusLoading] = useState(false);
    const [selectedMemberInfo, setSelectedMemberInfo] = useState<{
        status: string;
        memberCode: string;
        activatedAt: string;
        remainingVisits: string;
        expiryDate: string;
    } | null>(null);
    const [bookingInfo, setBookingInfo] = useState<BookingInfo>({
        tanggal: null,
        jumlahPemain: 0,
        daftarPemain: [],
        pemainByLevel: {}
    });
    const latestBookingRequestId = useRef(0);
    const selectedBookingDate = useRef(formData.tanggal);
    const [confirmationBookingInfo, setConfirmationBookingInfo] = useState<BookingInfo | null>(null);
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const [localBooking, setLocalBooking] = useState<{ nama: string; level: string } | null>(null);

    useEffect(() => {
        selectedBookingDate.current = formData.tanggal;
    }, [formData.tanggal]);

    const fetchBookingNames = useCallback(async (specificDate?: string): Promise<BookingInfo | null> => {
        if (!id) return null;
        const requestId = ++latestBookingRequestId.current;
        try {
            setLoadingBookings(true);
            const requestDate = specificDate || (selectedBookingDate.current !== '__MEMBER_1_BULAN__' ? selectedBookingDate.current : '');
            const dateQuery = requestDate ? `?date=${encodeURIComponent(requestDate)}` : '';
            const response = await axios.get<BookingInfo>(`${API_ENDPOINTS.GOR_BOOKINGS(id)}${dateQuery}`);
            const responseData = response.data || ({} as BookingInfo);
            const normalizedBookingInfo: BookingInfo = {
                ...responseData,
                tanggal: responseData.tanggal ?? null,
                jumlahPemain: Number(responseData.jumlahPemain) || 0,
                jumlahBookingJadwal: Number(responseData.jumlahBookingJadwal ?? responseData.jumlahPemain) || 0,
                akumulasiDiskon: Number(responseData.akumulasiDiskon) || 0,
                daftarPemain: Array.isArray(responseData.daftarPemain) ? responseData.daftarPemain : [],
                pemainByLevel: responseData.pemainByLevel && typeof responseData.pemainByLevel === 'object'
                    ? Object.fromEntries(Object.entries(responseData.pemainByLevel).filter(([, names]) => Array.isArray(names)))
                    : {},
            };
            if (requestId === latestBookingRequestId.current) {
                setBookingInfo(normalizedBookingInfo);
            }
            return normalizedBookingInfo;
        } catch (error) {
            console.error('Error fetching booking names:', error);
            return null;
        } finally {
            if (requestId === latestBookingRequestId.current) {
                setLoadingBookings(false);
            }
        }
    }, [id]);

    useEffect(() => {
        if (!formData.tanggal || formData.tanggal === '__MEMBER_1_BULAN__') return;
        fetchBookingNames(formData.tanggal);
    }, [fetchBookingNames, formData.tanggal]);

    useEffect(() => {
        const currentUserEmail = user?.email;
        if (!currentUserEmail) return;

        let active = true;
        fetchAdminAccessData()
            .then(data => {
                if (!active) return;
                const normalizedEmail = currentUserEmail.trim().toLowerCase();
                const adminEmails = (data.adminEmails || []).map((e: string) => (e || '').toString().trim().toLowerCase());
                const superEmails = (data.superadminEmails || []).map((e: string) => (e || '').toString().trim().toLowerCase());
                const isAdmin = adminEmails.includes(normalizedEmail) || superEmails.includes(normalizedEmail);
                setIsAdminUser(isAdmin);
                setIsSuperAdmin(superEmails.includes(normalizedEmail));
                // If user is admin, refresh booking list to ensure bayar-di-lapang items are present
                if (isAdmin) {
                    try {
                        fetchBookingNames();
                    } catch (e) {
                        console.error('Error refreshing booking names after admin fetch:', e);
                    }
                }
            })
            .catch(error => {
                console.error('Error refreshing admin access data:', error);
            });

        return () => {
            active = false;
        };
    }, [user?.email, fetchBookingNames]);
    const [showCalendarDialog, setShowCalendarDialog] = useState(false);
    const [calendarEvent, setCalendarEvent] = useState<CalendarEvent | null>(null);
    const [showBlockedLevelDialog, setShowBlockedLevelDialog] = useState(false);
    const [blockedLevelMsg, setBlockedLevelMsg] = useState("");
    const [showKapasitasDialog, setShowKapasitasDialog] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(true);
    const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
    const [confirmedBookingPricing, setConfirmedBookingPricing] = useState<{ finalPrice: number; finalDiscountPercentage: number } | null>(null);
    const [paymentRetryCount, setPaymentRetryCount] = useState(0);
    const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState(false);
    const [showPaymentLoadingDialog, setShowPaymentLoadingDialog] = useState(false);

    // Tambahkan state di bagian state utama GorDetail
    const [editDialog, setEditDialog] = useState<{open: boolean, player: string, level: string, statusBayar: string, levelList: string[], playerEmail?: string, idBooking?: string} | null>(null);
    const [editField, setEditField] = useState<{nama?: string, level?: string, statusBayar?: string}>({});
    const [editLoading, setEditLoading] = useState(false);

    const parseCurrency = useCallback((value?: string | number | null) => {
        if (value === null || value === undefined || value === '') return 0;
        const normalizedValue = value.toString().replace(/[^\d]/g, '');
        const parsed = Number(normalizedValue || '0');
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            maximumFractionDigits: 0,
        }).format(value);
    }, []);

    const parseScheduleDate = useCallback((selectedDate: string, scheduleTime?: string | null) => {
        if (!selectedDate) return null;
        const dateValue = selectedDate.trim();
        const timeValue = (scheduleTime || gor?.jam || '19.00').split('-')[0].trim();
        const cleanTime = timeValue.replace(/[:.]/g, ':').replace(/\s+/g, '');
        const timeMatch = cleanTime.match(/^(\d{1,2}):(\d{2})$/);
        const parsedHour = timeMatch ? Number(timeMatch[1]) : 19;
        const parsedMinute = timeMatch ? Number(timeMatch[2]) : 0;

        const monthMap: { [key: string]: number } = {
            'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
            'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
        };

        const match = dateValue.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
        if (match) {
            const [, day, monthName, year] = match;
            const monthIndex = monthMap[monthName];
            if (monthIndex !== undefined) {
                return new Date(Date.UTC(Number(year), monthIndex, Number(day), parsedHour, parsedMinute) - (7 * 60 * 60 * 1000));
            }
        }

        const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), parsedHour, parsedMinute) - (7 * 60 * 60 * 1000));
        }

        return null;
    }, [gor?.jam]);

    const pricingPreview = useMemo(() => {
        if (!formData.tanggal || !gor) return null;
        if (bookingInfo.tanggal !== formData.tanggal) return null;

        const normalPrice = parseCurrency(gor.biaya);
        const capacity = Number(gor.kapasitasMaksimal || bookingInfo.kapasitasMaksimal || 0);
        const currentParticipants = Number(bookingInfo.jumlahPemain || 0);
        const bookingCount = Number(bookingInfo.jumlahBookingJadwal ?? currentParticipants);
        const accumulatedDiscountAmount = Number(bookingInfo.akumulasiDiskon || 0);
        const scheduleStartAt = parseScheduleDate(formData.tanggal, gor.jam);

        if (!scheduleStartAt || !normalPrice || !capacity) return null;

        return calculateBookingPrice({
            normalPrice,
            capacity,
            currentParticipants,
            bookingCount,
            accumulatedDiscountAmount,
            scheduleStartAt,
            bookingAt: new Date(),
        });
    }, [bookingInfo.jumlahPemain, bookingInfo.jumlahBookingJadwal, bookingInfo.akumulasiDiskon, bookingInfo.kapasitasMaksimal, bookingInfo.tanggal, formData.tanggal, gor, parseCurrency, parseScheduleDate]);
    const displayedPrice = confirmedBookingPricing?.finalPrice ?? pricingPreview?.finalPrice;
    const displayedDiscountPercentage = confirmedBookingPricing?.finalDiscountPercentage ?? pricingPreview?.finalDiscountPercentage;


    // Urutan level untuk validasi
    const LEVEL_ORDER = ['Prelevel', 'Beginner', 'Intermediate', 'Advanced'];
    const LEVELS = ['Prelevel', 'Beginner', 'Intermediate', 'Advanced'];

    const fetchGorData = useCallback(async () => {
            try {
                setLoading(true);
                setError(null);
                // Jika data sudah ada di location state, gunakan itu
                if (location.state?.gor) {
                    setGor(location.state.gor);
                    setLoading(false);
                    return;
                }
                // Jika tidak, ambil dari API
                if (!id) {
                    setError('ID GOR tidak valid');
                    setLoading(false);
                    return;
                }
                const response = await axios.get<Gor>(API_ENDPOINTS.GOR_DETAIL(id));
                if (response.data) {
                    setGor(response.data);
                } else {
                    setError('Data GOR tidak ditemukan');
                }
            } catch (error: any) {
                setError(error.response?.data?.message || 'Gagal memuat data GOR');
            } finally {
                setLoading(false);
            }
        }, [id, location.state]);

    useEffect(() => {
        fetchGorData();
        fetchBookingNames();

        // Update email dari user yang login
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser?.email) {
            setFormData(prev => ({
                ...prev,
                email: currentUser.email
            }));
        }
    }, [id, location.state, fetchBookingNames, fetchGorData]);

    useEffect(() => {
        if (gor?.tanggalTersedia?.length && !formData.tanggal) {
            setFormData(prev => ({
                ...prev,
                tanggal: gor.tanggalTersedia[0]
            }));
        }
    }, [gor, formData.tanggal]);

    const fetchMemberStatus = async () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const email = user?.email;
        if (!email) {
            setMemberInfo([]);
            return;
        }

        setMemberStatusLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.MEMBERSHIP_AND_BOOKING, {
                params: { email }
            });

            if (response.data?.success && Array.isArray(response.data.memberships)) {
                // Filter hanya member dengan sisa > 0 dan hitung expiry date
                const activeMembers = response.data.memberships
                    .filter((member: any) => {
                        const remaining = parseInt(member.remainingVisits, 10);
                        return !isNaN(remaining) && remaining > 0;
                    })
                    .map((member: any) => {
                        // Hitung batas tanggal mabar (27 hari dari tanggal pembelian)
                        const purchaseDate = new Date(member.activatedAt);
                        const expiryDate = new Date(purchaseDate.getTime() + (27 * 24 * 60 * 60 * 1000));
                        
                        // Format expiry date ke format Indonesia
                        const expiryDateFormatted = expiryDate.toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        });
                        
                        return {
                            ...member,
                            expiryDate: expiryDateFormatted
                        };
                    });
                setMemberInfo(activeMembers);
                // Update selectedMemberInfo if memberCode is still selected
                if (memberCode) {
                    const updatedMember = activeMembers.find((m: any) => m.memberCode === memberCode);
                    if (updatedMember) {
                        setSelectedMemberInfo(updatedMember);
                    } else {
                        // Jika member yang dipilih sudah habis, reset
                        setSelectedMemberInfo(null);
                        setMemberCode('');
                    }
                }
            } else {
                setMemberInfo([]);
            }
        } catch (error) {
            console.error('Error fetching membership status:', error);
            setMemberInfo([]);
        } finally {
            setMemberStatusLoading(false);
        }
    };

    useEffect(() => {
        fetchMemberStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Simpan jumlah pemain dan kapasitas maksimal ke localStorage setiap kali bookingInfo berubah
    useEffect(() => {
        if (bookingInfo && bookingInfo.tanggal) {
            localStorage.setItem(`jumlahPemain_${id}_${bookingInfo.tanggal}`, bookingInfo.jumlahPemain.toString());
            if (bookingInfo.kapasitasMaksimal !== undefined) {
                localStorage.setItem(`kapasitasMaksimal_${id}_${bookingInfo.tanggal}`, bookingInfo.kapasitasMaksimal.toString());
            }
        }
    }, [bookingInfo, id]);

    const bayarDiLapanganNameSet = new Set<string>(
        [
            ...(bookingInfo.daftarPemainBayarDiLapanganDetail?.map(item => item.nama || '') ?? []),
            ...(bookingInfo.daftarPemainBayarDiLapangan ?? [])
        ]
            .map(name => name?.trim().toLowerCase())
            .filter(Boolean)
    );

    const bayarDiLapCount = (bookingInfo.daftarPemainBayarDiLapanganDetail?.length ?? bookingInfo.daftarPemainBayarDiLapangan?.length ?? 0);

    const filteredPemainByLevel = isAdminUser
        ? Object.entries(bookingInfo.pemainByLevel || {}).reduce<Record<string, string[]>>((acc, [level, pemainList]) => {
            const filtered = pemainList.filter(nama => !bayarDiLapanganNameSet.has(nama?.trim().toLowerCase()));
            if (filtered.length > 0) {
                acc[level] = filtered;
            }
            return acc;
        }, {})
        : (bookingInfo.pemainByLevel || {});

    const filteredPemainByLevelEntries = Object.entries(filteredPemainByLevel);

    // Hitung jumlah pemain yang benar-benar sudah bayar (exclude yang Bayar di Lapang)
    const paidCount = Object.entries(bookingInfo.pemainByLevel || {}).reduce((sum, [, pemainList]) => {
        const cnt = (pemainList || []).filter(n => !bayarDiLapanganNameSet.has((n || '').trim().toLowerCase())).length;
        return sum + cnt;
    }, 0);

    const daftarPemainTotalCount = isAdminUser ? paidCount : paidCount + bayarDiLapCount;

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Jika belum login, redirect ke login
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!currentUser?.email) {
            // Simpan URL saat ini untuk redirect setelah login
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            navigate('/login');
            return;
        }
        
        // Cek status blokir akun terlebih dahulu
        if (formData.email) {
            try {
                const response = await axios.get(`/api/bookings?action=check-blocked&email=${encodeURIComponent(formData.email)}`);
                if (response.data.isBlocked) {
                    setError('Maaf, akun Anda telah diblokir dan tidak dapat melakukan booking. Silakan hubungi admin untuk informasi lebih lanjut.');
                    return;
                }
            } catch (error) {
                console.error('Error checking blocked status:', error);
                // Lanjutkan booking jika error (fallback untuk availability)
            }
        }
        
        // Validasi level minimal booking
        const minimal = gor?.levelMinimal || 'Prelevel';
        const userLevelIdx = LEVEL_ORDER.indexOf(formData.levelBermain);
        const minimalIdx = LEVEL_ORDER.indexOf(minimal);
        if (minimalIdx > -1 && userLevelIdx > -1 && userLevelIdx < minimalIdx) {
            setBlockedLevelMsg(`Maaf, level minimal untuk booking di GOR ini adalah ${minimal}. Silakan pilih GOR lain atau naikkan level Anda.`);
            setShowBlockedLevelDialog(true);
            return;
        }

        if (useMemberCode) {
            setMemberCodeError('');
            if (!memberCode.trim()) {
                setMemberCodeError('Kode member wajib diisi saat memilih Bayar dengan Kode Member.');
                return;
            }

                try {
                const bookingDateParam = formData.tanggal ? `&bookingDate=${encodeURIComponent(formData.tanggal)}` : '';
                const validationResponse = await axios.get(`/api/bookings?action=validate-member-code&memberCode=${encodeURIComponent(memberCode.trim())}&email=${encodeURIComponent(currentUser.email)}${bookingDateParam}`);
                if (!validationResponse.data?.success) {
                    setMemberCodeError(validationResponse.data?.message || 'Maaf kode member tidak valid!');
                    return;
                }
            } catch (validationError: any) {
                setMemberCodeError(validationError.response?.data?.message || 'Maaf kode member tidak valid!');
                return;
            }
        } else {
            setMemberCodeError('');
        }

        // Hapus pengecekan kapasitas penuh karena sekarang boleh booking untuk waitinglist
        // Ketika kapasitas penuh, booking akan otomatis masuk ke waitinglist
        let selectedDateBookingInfo: BookingInfo | null = bookingInfo;
        if (formData.tanggal !== '__MEMBER_1_BULAN__' && bookingInfo.tanggal !== formData.tanggal) {
            selectedDateBookingInfo = await fetchBookingNames(formData.tanggal);
        }
        if (formData.tanggal !== '__MEMBER_1_BULAN__' && selectedDateBookingInfo?.tanggal !== formData.tanggal) {
            setError('Gagal memuat daftar pemain untuk tanggal yang dipilih. Silakan coba lagi.');
            return;
        }
        setConfirmationBookingInfo(selectedDateBookingInfo);
        setLocalBooking({ nama: formData.nama.trim(), level: formData.levelBermain });
        setShowConfirmationDialog(true);
    };

    const handleConfirmBooking = async () => {
        setPendingSubmit(true);
        setConfirmedBookingPricing(null);
        setSuccess(false);
        // Cek apakah user sudah login
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!currentUser?.email) {
            // Simpan URL saat ini untuk redirect setelah login
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            setShowConfirmationDialog(false);
            navigate('/login');
            setPendingSubmit(false);
            return;
        }
        setLoading(true);
        setError(null);
        // Trim nama untuk menghilangkan whitespace di depan dan belakang
        const trimmedNama = formData.nama.trim();
        
        if (!formData.tanggal || !trimmedNama || !formData.levelBermain || !formData.nomorHP) {
            setError('Semua field harus diisi');
            setLoading(false);
            setPendingSubmit(false);
            return;
        }

        // Jika member 1 bulan, booking semua tanggal lalu lanjutkan ke dialog pembayaran
        if (formData.tanggal === "__MEMBER_1_BULAN__") {
            try {
                // Generate unique order_id untuk semua bookings member
                const memberOrderId = `MEMBER-MABAR-${Date.now()}`;
                for (const tanggal of gor?.tanggalTersedia || []) {
                    const bookingData = {
                        tanggal,
                        tempat: gor?.nama,
                        nama: trimmedNama,
                        levelBermain: formData.levelBermain,
                        email: currentUser.email,
                        nomorHP: formData.nomorHP,
                        gross_amount: gor?.biaya ? parseInt(gor.biaya) : 100000,
                        order_id: memberOrderId
                    };
                    await axios.post(API_ENDPOINTS.BOOKINGS, bookingData);
                }
                setShowConfirmationDialog(false);
                localStorage.setItem('lastBooking', JSON.stringify({
                    nama: trimmedNama,
                    level: formData.levelBermain,
                    nomorHP: formData.nomorHP
                }));
                // Set paymentOrderId ke memberOrderId untuk Midtrans
                setPaymentOrderId(memberOrderId);
                // Start Midtrans payment flow dengan memberOrderId langsung
                setTimeout(() => {
                    startMidtransPayment(memberOrderId);
                }, 300);
            } catch (err) {
                setError('Gagal booking member 1 bulan. Silakan coba lagi.');
            } finally {
                setLoading(false);
                setPendingSubmit(false);
            }
            return;
        }
        const bookingData: any = {
            tanggal: formData.tanggal,
            tempat: gor?.nama,
            nama: trimmedNama,
            levelBermain: formData.levelBermain,
            email: currentUser.email,
            nomorHP: formData.nomorHP,
            gross_amount: gor?.biaya ? parseInt(gor.biaya) : 100000
        };
        if (useMemberCode) {
            bookingData.useMemberCode = true;
            bookingData.memberCode = memberCode.trim();
        }
        try {
            const response = await axios.post(API_ENDPOINTS.BOOKINGS, bookingData);
            if (response.data.success) {
                if (response.data.useMemberCode) {
                    setSuccess(true);
                    await fetchGorData();
                    await fetchMemberStatus();
                    setShowConfirmationDialog(false);
                    localStorage.setItem('lastBooking', JSON.stringify({
                      nama: trimmedNama,
                      level: formData.levelBermain,
                      nomorHP: formData.nomorHP
                    }));
                    fetchBookingNames(formData.tanggal);
                    window.dispatchEvent(new CustomEvent('booking-updated'));
                    return;
                }

                if (response.data.order_id) {
                    setPaymentOrderId(response.data.order_id);
                } else {
                    // Fallback jika order_id tidak ada di response
                    setPaymentOrderId(`RKTRS-${Date.now()}`);
                }
                if (Number.isFinite(Number(response.data.finalPrice))) {
                    setConfirmedBookingPricing({
                        finalPrice: Number(response.data.finalPrice),
                        finalDiscountPercentage: Number(response.data.finalDiscountPercentage) || 0,
                    });
                }
                setSuccess(true);
                await fetchGorData();
                setShowConfirmationDialog(false);
                localStorage.setItem('lastBooking', JSON.stringify({
                  nama: trimmedNama,
                  level: formData.levelBermain,
                  nomorHP: formData.nomorHP
                }));
                const tanggalStr = formData.tanggal;
                const jamStr = gor?.jam || '19.00-21.00';
                let start = new Date();
                let end = new Date();
                try {
                    const [tgl, bulan, tahun] = tanggalStr.split(' ');
                    const bulanMap: { [key: string]: number } = {
                        'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
                        'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
                    };
                    const jamRange = jamStr.split('-');
                    const [jamMulai, menitMulai] = jamRange[0].split('.')
                    const [jamSelesai, menitSelesai] = (jamRange[1] || '21.00').split('.')
                    start = new Date(parseInt(tahun), bulanMap[bulan as keyof typeof bulanMap], parseInt(tgl), parseInt(jamMulai), parseInt(menitMulai || '0'));
                    end = new Date(parseInt(tahun), bulanMap[bulan as keyof typeof bulanMap], parseInt(tgl), parseInt(jamSelesai), parseInt(menitSelesai || '0'));
                } catch {}
                setCalendarEvent({
                    title: `Booking Mabar di ${gor?.nama}`,
                    description: `Main bareng di ${gor?.nama} bersama RaketerS. Level: ${formData.levelBermain}`,
                    location: gor?.alamat || '',
                    start,
                    end
                });
                setShowCalendarDialog(true);
                fetchBookingNames(formData.tanggal);
                // ABIS BOOKING SUKSES, TRIGGER RELOAD FLOATING STATUS
                window.dispatchEvent(new CustomEvent('booking-updated'));
            } else {
                setError('Gagal melakukan booking');
            }
        } finally {
            setLoading(false);
            setPendingSubmit(false);
        }
    };

    const handleCloseConfirmation = () => {
        setShowCancelDialog(true);
    };

    const handleCancelBooking = () => {
        setShowConfirmationDialog(false);
        setShowCancelDialog(false);
    };

    const handleLoginClick = () => {
        setShowLoginDialog(false);
        navigate('/login');
    };

    // Trigger pembayaran Midtrans (QRIS dinamis) setelah booking sukses
    const startMidtransPayment = async (overrideOrderId?: string, isRetry: boolean = false) => {
        setShowPaymentLoadingDialog(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseAmount =
                formData.tanggal === "__MEMBER_1_BULAN__"
                    ? (gor?.biaya && gor.biaya !== '-' ? parseInt(gor.biaya) * (gor?.tanggalTersedia?.length || 4) : 100000 * (gor?.tanggalTersedia?.length || 4))
                    : (gor?.biaya && gor.biaya !== '-' ? parseInt(gor.biaya) : 100000);

            if (!currentUser?.email) {
                // Jika tidak ada email (belum login), tampilkan pesan error
                alert('Silakan login terlebih dahulu untuk melakukan pembayaran');
                return;
            }

            const orderId = overrideOrderId || paymentOrderId || `RKTRS-${Date.now()}`;
            const paymentAmount = formData.tanggal !== "__MEMBER_1_BULAN__" && confirmedBookingPricing
                ? confirmedBookingPricing.finalPrice
                : formData.tanggal !== "__MEMBER_1_BULAN__" && pricingPreview
                ? pricingPreview.finalPrice
                : baseAmount;

            const response = await axios.post('/api/payment/snap-token', {
                order_id: orderId,
                gross_amount: paymentAmount,
                nama: formData.nama || currentUser?.displayName || 'Pemain',
                email: currentUser.email,
                finish_redirect_url: `${window.location.origin}/booking-sukses`
            });

            const redirectUrl = response.data?.redirect_url;
            const snapToken = response.data?.token;
            if (redirectUrl && snapToken) {
                // Simpan snap token dan redirect URL untuk akses ulang
                localStorage.setItem(`payment_${orderId}`, JSON.stringify({
                    snapToken,
                    redirectUrl,
                    orderId,
                    createdAt: Date.now()
                }));
                // Reset retry count jika berhasil
                setPaymentRetryCount(0);
                window.location.href = redirectUrl;
            } else {
                // Jika gagal dapat URL, coba lagi atau tampilkan error dialog
                handlePaymentFailure();
            }
        } catch (err) {
            console.error('Gagal memulai pembayaran Midtrans:', err);
            setShowPaymentLoadingDialog(false);
            handlePaymentFailure();
        }
    };

    const handlePaymentFailure = () => {
        const newRetryCount = paymentRetryCount + 1;
        setPaymentRetryCount(newRetryCount);
        setPaymentOrderId(null); // Reset order ID untuk attempt baru

        if (newRetryCount < 2) {
            // Coba lagi otomatis setelah 2 detik
            setTimeout(() => {
                startMidtransPayment(undefined, true);
            }, 2000);
        } else {
            // Sudah coba 2 kali, tampilkan dialog error
            setShowPaymentLoadingDialog(false);
            setShowPaymentErrorDialog(true);
        }
    };

    const handleRetryPayment = () => {
        setShowPaymentErrorDialog(false);
        setPaymentRetryCount(0);
        startMidtransPayment();
    };

    // Fungsi untuk generate file .ics dan download
    const handleAddToCalendar = () => {
        if (!calendarEvent) return;
        const pad = (n: number) => n.toString().padStart(2, '0');
        const formatDate = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
        const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${calendarEvent.title}\nDESCRIPTION:${calendarEvent.description}\nLOCATION:${calendarEvent.location}\nDTSTART:${formatDate(calendarEvent.start)}\nDTEND:${formatDate(calendarEvent.end)}\nEND:VEVENT\nEND:VCALENDAR`;
        const blob = new Blob([ics], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'booking-mabar.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setShowCalendarDialog(false);
        // Setelah tambah ke kalender, langsung lanjut ke pembayaran Midtrans
        startMidtransPayment();
    };

    const handleSkipCalendar = () => {
        setShowCalendarDialog(false);
        // Jika user melewati kalender, tetap lanjut ke pembayaran Midtrans
        startMidtransPayment();
    };

    // Tambahkan function handleEditPlayer - menggunakan ID booking sebagai referensi utama
    const handleEditPlayer = (idBooking: string) => {
      // Cek apakah user adalah superadmin
      const isSuperAdminUser = user?.email ? isSuperAdminEmail(user.email) : false;
      if (!isSuperAdminUser || !idBooking) return;
      
      // Cari data booking berdasarkan ID booking (unik, tidak mungkin duplikat)
      let playerData: { nama: string; level: string; email: string; statusBayar: string; idBooking: string } | null = null;
      
      // Cari di daftarPemainDetail
      if (bookingInfo && Array.isArray(bookingInfo.daftarPemainDetail)) {
        const found = bookingInfo.daftarPemainDetail.find(x => x.idBooking === idBooking);
        if (found && found.idBooking) {
          playerData = {
            nama: found.nama,
            level: found.level,
            email: found.email,
            statusBayar: found.statusBayarText || (found.statusBayar ? 'Sudah bayar' : 'Belum bayar'),
            idBooking: found.idBooking
          };
        }
      }
      
      // Jika tidak ketemu di daftarPemainDetail, cek di waitinglist
      if (!playerData && bookingInfo && Array.isArray(bookingInfo.daftarWaitinglistDetail)) {
        const found = bookingInfo.daftarWaitinglistDetail.find(x => x.idBooking === idBooking);
        if (found && found.idBooking) {
          playerData = {
            nama: found.nama,
            level: found.level,
            email: found.email,
            statusBayar: found.statusBayarText || (found.statusBayar ? 'Sudah bayar' : 'Belum bayar'),
            idBooking: found.idBooking
          };
        }
      }
      
      if (!playerData) {
        console.error('Booking tidak ditemukan dengan ID:', idBooking);
        return;
      }
      
      setEditDialog({
        open: true,
        player: playerData.nama,
        level: playerData.level,
        statusBayar: playerData.statusBayar,
        levelList: LEVELS,
        playerEmail: playerData.email,
        idBooking: playerData.idBooking
      });
      setEditField({
        nama: playerData.nama,
        level: playerData.level,
        statusBayar: playerData.statusBayar
      });
    };

    if (loading) {
        return (
            <>
                <Helmet>
                    <title>Detail GOR - RaketerS</title>
                </Helmet>
                <Container>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                        <CircularProgress />
                    </Box>
                </Container>
            </>
        );
    }

    // Hanya tampilkan error global jika data GOR tidak ditemukan
    if (!gor) {
        return (
            <>
                <Helmet>
                    <title>Detail GOR - RaketerS</title>
                </Helmet>
                <Container>
                    <Alert severity="error">Data GOR tidak ditemukan</Alert>
                </Container>
            </>
        );
    }

    const confirmationLevels: string[] = Array.from(new Set([
        ...Object.keys(confirmationBookingInfo?.pemainByLevel || {}),
        localBooking?.level || formData.levelBermain
    ]));

    // Superadmin: akses penuh (edit pemain, hapus booking, dll)
    // const isSuperAdmin = user?.email ? isSuperAdminEmail(user.email) : false; // already declared above
    // Admin: akses terbatas (hanya edit live score dan lihat warna hijau pembayaran)
    // Admin atau Superadmin: bisa lihat warna berbeda untuk yang sudah bayar/belum
    // (Superadmin punya semua akses admin, jadi juga bisa lihat warna hijau)

    const handleEditSubmit = async () => {
        if (!gor || !bookingInfo.tanggal || !editDialog || !editDialog.idBooking) return;
        setEditLoading(true);
        // ID booking sudah cukup sebagai referensi (unik, tidak mungkin duplikat)
        const idBooking = editDialog.idBooking;
        try {
            // Edit nama
            if (editField.nama !== editDialog.player) {
                await axios.post('/api/bookings?action=edit', {
                    idBooking,
                    field: 'nama',
                    value: editField.nama
                });
            }
            // Edit level
            if (editField.level && editField.level !== editDialog.level) {
                await axios.post('/api/bookings?action=edit', {
                    idBooking,
                    field: 'level',
                    value: editField.level
                });
            }
            // Edit status bayar
            if (typeof editField.statusBayar === 'string' && editField.statusBayar.length > 0) {
                await axios.post('/api/bookings?action=edit', {
                    idBooking,
                    field: 'statusBayar',
                    value: editField.statusBayar
                });
            }
            setEditDialog(null);
            fetchBookingNames(formData.tanggal);
        } catch (e) {
            alert('Gagal update. Pastikan Anda punya akses.');
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditDelete = async () => {
        if (!gor || !bookingInfo.tanggal || !editDialog || !editDialog.idBooking) return;
        setEditLoading(true);
        try {
            await axios.post('/api/bookings?action=cancel', {
                idBooking: editDialog.idBooking, // ID booking sudah cukup (unik, tidak mungkin duplikat)
                email: user?.email || '' // Email hanya untuk validasi akses
            });
            setEditDialog(null);
            fetchBookingNames(formData.tanggal);
        } catch (e) {
            alert('Gagal hapus booking.');
        } finally {
            setEditLoading(false);
        }
    };

    const generateShareText = async () => {
        if (!gor || !bookingInfo.tanggal) return '';

        return buildShareText({
            gor,
            bookingInfo,
            currentDateLabel: getHariFromTanggal(bookingInfo.tanggal),
            pricing: pricingPreview,
        });
    };

    const handleShare = () => {
        if (!gor || !bookingInfo.tanggal) {
            setError('Tidak ada informasi booking untuk dibagikan');
            return;
        }
        setShowShareDialog(true);
    };

    const shareToWhatsApp = async () => {
        const shareText = await generateShareText();
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToTwitter = async () => {
        const shareText = await generateShareText();
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToFacebook = async () => {
        const shareText = await generateShareText();
        const currentUrl = window.location.href;
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToTelegram = async () => {
        const shareText = await generateShareText();
        const currentUrl = window.location.href;
        const url = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const copyToClipboard = async () => {
        const shareText = await generateShareText();
        try {
            await navigator.clipboard.writeText(shareText);
            setShareSnackbarOpen(true);
            setShowShareDialog(false);
        } catch (err) {
            // Fallback untuk browser lama
            const textArea = document.createElement('textarea');
            textArea.value = shareText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setShareSnackbarOpen(true);
                setShowShareDialog(false);
            } catch (e) {
                setError('Gagal menyalin teks. Silakan salin manual.');
            }
            document.body.removeChild(textArea);
        }
    };

    return (
        <>
            <Helmet>
                <title>{gor.nama} - RaketerS</title>
            </Helmet>

            <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
                <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: isMobile ? 2 : 3,
                            background: theme.palette.mode === 'dark' 
                                ? (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)')
                                : undefined,
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(99,170,255,0.22)'
                                : undefined,
                            borderRadius: 3
                        }}>
                            <Typography 
                                variant={isMobile ? "h5" : "h4"} 
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {gor?.nama}
                            </Typography>
                            {/* Gambar GOR dengan overlay PREREGISTRATION jika Pandiga atau KONI setiap Rabu */}
                            <div style={{ position: 'relative', width: '100%' }}>
                              <img
                                src={gor?.gambar}
                                alt={gor?.nama}
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    marginBottom: isMobile ? '0.5rem' : '1rem',
                                    borderRadius: '8px'
                                }}
                              />
                              {/* Overlay PREREGISTRATION */}
                              {/* Dihapus sesuai permintaan */}
                            </div>
                            {/* Info eksklusif untuk GOR yang mendukung ranked match - di bawah gambar */}
                            {gor?.nama && hasRankedMatch(gor.nama) && (
                                <Box sx={{ 
                                    mb: 2, 
                                    p: 2, 
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 183, 77, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                                    border: `2px solid ${theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00'}`,
                                    borderRadius: 2,
                                    textAlign: 'center'
                                }}>
                                    <Typography 
                                        variant={isMobile ? "h6" : "h5"} 
                                        sx={{ 
                                            fontWeight: 700, 
                                            color: theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
                                            mb: 1
                                        }}
                                    >
                                        🏆 Mendukung Program Ranked Match
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
                                            fontWeight: 500
                                        }}
                                    >
                                        GOR ini mendukung sistem peringkat Elo untuk meningkatkan kompetitifitas permainan badminton Anda!
                                    </Typography>
                                </Box>
                            )}
                            {gor?.jam && gor.jam !== '-' && (
                                <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                                    Jam Main: {gor.jam}
                                </Typography>
                            )}
                            <Box>
  {gor?.deskripsi && gor.deskripsi.length > 50 ? (
    showFullDescription ? (
      <>
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          paragraph
          sx={{ whiteSpace: 'pre-line', mb: 0.5 }}
        >
          {gor.deskripsi}
        </Typography>
        <Button
          size="small"
          onClick={() => setShowFullDescription(false)}
          sx={{
            textTransform: 'none',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            fontWeight:'bold',
            mb: 1,
            color: '#42a5f5',
            background: 'transparent',
            minWidth:'unset',
            p:0
          }}
        >
          Show less
        </Button>
      </>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          paragraph
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: 0,
            flex: 1,
            pr: 1
          }}
        >
          {gor.deskripsi}
        </Typography>
        <Button
          size="small"
          onClick={() => setShowFullDescription(true)}
          sx={{
            textTransform: 'none', fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight:'bold', minWidth: 'unset',
            color: '#42a5f5',
            p: 0, ml: 1, background: 'transparent'
          }}
        >
          Show more
        </Button>
      </Box>
    )
  ) : (
    <Typography variant={isMobile ? 'body2' : 'body1'} paragraph>{gor?.deskripsi}</Typography>
  )
}</Box>
                            <Typography variant={isMobile ? "body2" : "body1"}>
                                <strong>Alamat:</strong> {renderAddressWithMapLink(gor?.alamat)}
                            </Typography> 
                            <Typography variant="body2" color="text.secondary">
                                Pemain Utama: {bookingInfo.jumlahPemain}/{bookingInfo.kapasitasMaksimal ?? gor.kapasitasMaksimal}
                                {(bookingInfo.jumlahWaitinglist ?? 0) > 0 && (
                                    <span style={{ marginLeft: '8px', color: '#ff9800' }}>
                                        • Reservasi: {bookingInfo.jumlahWaitinglist}
                                    </span>
                                )}
                            </Typography>
                            {gor?.biaya && gor.biaya !== '-' && (
                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ color: 'primary.main', fontWeight: 700 }}>
                                    Biaya Mabar: Rp{parseInt(gor.biaya).toLocaleString('id-ID')}
                                </Typography>
                            )}
                            {gor?.levelMinimal && (
                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    Level Minimal: {gor.levelMinimal}
                                </Typography>
                            )}
                            
                            {/* Tombol Share - muncul ketika ada info booking */}
                            {bookingInfo.tanggal && (
                                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Tooltip title="Bagikan info booking terkini">
                                        <Button
                                            onClick={handleShare}
                                            variant="outlined"
                                            color="primary"
                                            size={isMobile ? "small" : "medium"}
                                            startIcon={<ShareIcon />}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Bagikan Info Booking
                                        </Button>
                                    </Tooltip>
                                </Box>
                            )}
                            
                            {/* Daftar nama booking */}
                            {bookingInfo.daftarPemain.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Daftar Pemain Mabar {bookingInfo.tanggal ? `${getHariFromTanggal(bookingInfo.tanggal)}, ${bookingInfo.tanggal}` : ''} ({daftarPemainTotalCount} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="success.main" sx={{ mb: 2, fontStyle: 'italic', fontWeight: 600 }}>
                                        ✅ Pemain yang terdaftar di sini sudah pasti berhak mengikuti mabar (sudah melakukan pembayaran).
                                    </Typography>
                                    {loadingBookings ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            {filteredPemainByLevelEntries.length > 0 ? (
                                                filteredPemainByLevelEntries.map(([level, pemainList]) => (
                                                    <Box key={level} sx={{ mb: 2 }}>
                                                        <Typography 
                                                            variant={isMobile ? "body2" : "body1"} 
                                                            sx={{ 
                                                                fontWeight: 'bold', 
                                                                mb: 1,
                                                                color: 'primary.main',
                                                                borderBottom: '2px solid',
                                                                borderColor: 'primary.main',
                                                                pb: 0.5
                                                            }}
                                                        >
                                                            Level {level} ({pemainList.length} pemain)
                                                        </Typography>
                                                        <Box sx={{ 
                                                            display: 'grid',
                                                            gridTemplateColumns: isMobile
                                                                ? 'repeat(2, 1fr)'
                                                                : pemainList.length >= 3
                                                                    ? 'repeat(3, 1fr)'
                                                                    : 'repeat(2, 1fr)',
                                                            gap: 1
                                                        }}>
                                                            {pemainList.map((nama, idx) => {
                                                                // Cari detail pemain dari daftarPemainDetail berdasarkan nama dan level
                                                                const pemainDetail = bookingInfo.daftarPemainDetail?.find(p => 
                                                                    p.nama?.trim() === nama?.trim() && 
                                                                    p.level?.trim() === level?.trim()
                                                                );
                                                                const idBooking = pemainDetail?.idBooking || '';
                                                                const memberType = (pemainDetail as any)?.memberType || null;
                                                                // Cek status pembayaran langsung dari data booking (lebih akurat)
                                                                return (
                                                                    <Tooltip key={idx} title={isSuperAdmin ? 'Edit pemain' : ''} arrow>
                                                                        <span style={{ display: 'block', width: '100%', position: 'relative' }}>
                                                                            {memberType && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: -8,
                                                                                        right: 8,
                                                                                        background: memberType === 'Elite' ? '#ff0000' : '#0000ff',
                                                                                        color: 'white',
                                                                                        px: 0.5,
                                                                                        py: 0.2,
                                                                                        borderRadius: 1,
                                                                                        fontWeight: 'bold',
                                                                                        fontSize: '0.7rem',
                                                                                        zIndex: 2,
                                                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                                                                    }}
                                                                                >
                                                                                    {memberType}
                                                                                </Typography>
                                                                            )}
                                                                            <Button
                                                                                variant="text"
                                                                                color="primary"
                                                                                size="small"
                                                                                startIcon={
                                                                                    isSuperAdmin
                                                                                      ? <EditIcon fontSize="small" />
                                                                                      : undefined
                                                                                }
                                                                                onClick={isSuperAdmin && idBooking ? () => handleEditPlayer(idBooking) : undefined}
                                                                                sx={{
                                                                                    position: memberType ? 'relative' : undefined,
                                                                                    overflow: memberType ? 'visible' : undefined,
                                                                                    textTransform: 'none',
                                                                                    minWidth: 0,
                                                                                    fontWeight: 600,
                                                                                    px: 1,
                                                                                    width: '100%',
                                                                                    minHeight: 32,
                                                                                    boxSizing: 'border-box',
                                                                                    justifyContent: 'flex-start',
                                                                                    alignItems: 'center',
                                                                                    textAlign: 'left',
                                                                                    // Admin dan superadmin bisa melihat warna hijau (superadmin punya semua akses admin)
                                                                                    background: memberType
                                                                                        ? memberType === 'Elite'
                                                                                            ? 'linear-gradient(135deg, rgba(255, 60, 60, 0.18), rgba(255, 20, 20, 0.08))'
                                                                                            : 'linear-gradient(135deg, rgba(40, 90, 255, 0.2), rgba(0, 40, 150, 0.08))'
                                                                                        : theme.palette.primary.light,
                                                                                    color: memberType
                                                                                        ? theme.palette.mode === 'dark'
                                                                                            ? '#e0e0e0'
                                                                                            : '#111'
                                                                                        : theme.palette.common.black,
                                                                                    borderRadius: 2,
                                                                                    zIndex: 1,
                                                                                    '&::before': memberType ? {
                                                                                        content: '""',
                                                                                        position: 'absolute',
                                                                                        inset: '-4px',
                                                                                        borderRadius: 6,
                                                                                        background: memberType === 'Elite'
                                                                                            ? 'radial-gradient(circle at 20% 20%, rgba(255,20,20,1), transparent 15%), radial-gradient(circle at 50% 12%, rgba(255,0,0,0.95), transparent 14%), radial-gradient(circle at 80% 30%, rgba(220,0,0,0.85), transparent 16%), radial-gradient(circle at 45% 85%, rgba(255,60,40,0.7), transparent 20%)'
                                                                                            : 'radial-gradient(circle at 20% 20%, rgba(40,100,255,1), transparent 15%), radial-gradient(circle at 50% 12%, rgba(10,70,255,0.95), transparent 14%), radial-gradient(circle at 80% 30%, rgba(0,50,220,0.85), transparent 16%), radial-gradient(circle at 45% 85%, rgba(60,120,255,0.7), transparent 20%)',
                                                                                        filter: 'blur(10px)',
                                                                                        opacity: 1,
                                                                                        pointerEvents: 'none',
                                                                                        zIndex: 0,
                                                                                        animation: 'fireFlicker 0.9s infinite ease-in-out'
                                                                                    } : undefined,
                                                                                    '&::after': memberType ? {
                                                                                        content: '""',
                                                                                        position: 'absolute',
                                                                                        inset: 0,
                                                                                        borderRadius: 2,
                                                                                        border: `2px solid ${memberType === 'Elite' ? '#ff0000' : '#0033cc'}`,
                                                                                        boxShadow: `0 0 18px ${memberType === 'Elite' ? 'rgba(255,0,0,0.95)' : 'rgba(0,51,204,0.95)'}, inset 0 0 10px ${memberType === 'Elite' ? 'rgba(255,80,80,0.7)' : 'rgba(70,130,255,0.65)'}`,
                                                                                        pointerEvents: 'none',
                                                                                        zIndex: 0,
                                                                                        animation: 'firePulse 1s infinite ease-in-out'
                                                                                    } : undefined,
                                                                                    '@keyframes fireFlicker': {
                                                                                        '0%': { opacity: 0.92, transform: 'scale(1)' },
                                                                                        '30%': { opacity: 1, transform: 'scale(1.02)' },
                                                                                        '60%': { opacity: 0.88, transform: 'scale(1.01)' },
                                                                                        '100%': { opacity: 0.92, transform: 'scale(1)' }
                                                                                    },
                                                                                    '@keyframes firePulse': {
                                                                                        '0%': { boxShadow: `0 0 16px ${memberType === 'Elite' ? 'rgba(255,20,20,0.95)' : 'rgba(0,40,180,0.95)'}, inset 0 0 8px ${memberType === 'Elite' ? 'rgba(255,120,120,0.75)' : 'rgba(80,140,255,0.7)'}` },
                                                                                        '50%': { boxShadow: `0 0 28px ${memberType === 'Elite' ? 'rgba(255,60,60,1)' : 'rgba(20,80,255,1)'}, inset 0 0 12px ${memberType === 'Elite' ? 'rgba(255,160,160,0.8)' : 'rgba(120,180,255,0.75)'}` },
                                                                                        '100%': { boxShadow: `0 0 16px ${memberType === 'Elite' ? 'rgba(255,20,20,0.95)' : 'rgba(0,40,180,0.95)'}, inset 0 0 8px ${memberType === 'Elite' ? 'rgba(255,120,120,0.75)' : 'rgba(80,140,255,0.7)'}` }
                                                                                    },
                                                                                    '&.Mui-disabled': {
                                                                                        color: memberType
                                                                                            ? theme.palette.mode === 'dark'
                                                                                                ? '#e0e0e0 !important'
                                                                                                : theme.palette.common.black + ' !important'
                                                                                            : theme.palette.common.black + ' !important',
                                                                                        background: memberType
                                                                                            ? memberType === 'Elite'
                                                                                                ? 'linear-gradient(135deg, rgba(255,60,60,0.18), rgba(255,20,20,0.08))'
                                                                                                : 'linear-gradient(135deg, rgba(40,90,255,0.2), rgba(0,40,150,0.08))'
                                                                                            : theme.palette.primary.light + ' !important',
                                                                                        opacity: 1,
                                                                                        WebkitTextFillColor: memberType
                                                                                            ? theme.palette.mode === 'dark'
                                                                                                ? '#e0e0e0'
                                                                                                : theme.palette.common.black
                                                                                            : theme.palette.common.black
                                                                                    }
                                                                                }}
                                                                                disabled={!isSuperAdmin}
                                                                            >
                                                                                {nama}
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                ))
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Semua pemain utama tercatat sebagai Bayar di Lapang; lihat daftar Bayar di Lapang untuk detail.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {isAdminUser && bookingInfo.daftarPemainBayarDiLapangan && bookingInfo.daftarPemainBayarDiLapangan.length > 0 && (
                                <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.action.hover, borderRadius: 2 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1, color: 'secondary.main' }}>
                                        Daftar Pemain Bayar di Lapang ({bookingInfo.daftarPemainBayarDiLapangan.length} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                        Daftar khusus ini hanya ditampilkan untuk Admin/Superadmin.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {(bookingInfo.daftarPemainBayarDiLapanganDetail?.length ? bookingInfo.daftarPemainBayarDiLapanganDetail : bookingInfo.daftarPemainBayarDiLapangan?.map((nama) => ({ nama, idBooking: undefined }))).map((item, idx) => {
                                            const idBooking = item?.idBooking || '';
                                            return (
                                                <Chip
                                                    key={idBooking || `${item.nama}-${idx}`}
                                                    label={item.nama}
                                                    size="small"
                                                    clickable={!!(isSuperAdmin && idBooking)}
                                                    onClick={isSuperAdmin && idBooking ? () => handleEditPlayer(idBooking) : undefined}
                                                    sx={{
                                                        backgroundColor: theme.palette.secondary.light,
                                                        color: theme.palette.secondary.contrastText,
                                                        fontWeight: 600,
                                                        cursor: isSuperAdmin && idBooking ? 'pointer' : 'default'
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {/* Daftar Waitinglist - Selalu tampilkan judul */}
                            {bookingInfo.tanggal && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1, color: 'warning.main' }}>
                                        Daftar Reservasi (Menunggu Pembayaran) {bookingInfo.tanggal ? `${getHariFromTanggal(bookingInfo.tanggal)}, ${bookingInfo.tanggal}` : ''} ({(bookingInfo.jumlahWaitinglist ?? 0)} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                        💡 Pemain reservasi masih berhak mengikuti mabar jika ada slot kosong.
                                    </Typography>
                                    {loadingBookings ? (
                                        <CircularProgress size={20} />
                                    ) : (bookingInfo.daftarWaitinglist && bookingInfo.daftarWaitinglist.length > 0) ? (
                                        <Box sx={{ mt: 2 }}>
                                            {Object.entries(bookingInfo.waitinglistByLevel || {}).map(([level, pemainList]) => (
                                                <Box key={level} sx={{ mb: 2 }}>
                                                    <Typography 
                                                        variant={isMobile ? "body2" : "body1"} 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            mb: 1,
                                                            color: 'warning.main',
                                                            borderBottom: '2px solid',
                                                            borderColor: 'warning.main',
                                                            pb: 0.5
                                                        }}
                                                    >
                                                        Level {level} ({pemainList.length} pemain)
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {pemainList.map((nama, idx) => {
                                                            // Cari detail pemain dari daftarWaitinglistDetail berdasarkan nama dan level
                                                            const pemainDetail = bookingInfo.daftarWaitinglistDetail?.find(p => 
                                                                p.nama?.trim() === nama?.trim() && 
                                                                p.level?.trim() === level?.trim()
                                                            );
                                                            const idBooking = pemainDetail?.idBooking || '';
                                                            // Cek status pembayaran langsung dari data booking (lebih akurat)
                                                            const isPaid = pemainDetail?.statusBayar || false;
                                                            // Hitung posisi global waitinglist berdasarkan daftarWaitinglistDetail (urut berdasarkan timestamp di server)
                                                            let posisiLabel = '';
                                                            try {
                                                                if (Array.isArray(bookingInfo.daftarWaitinglistDetail) && bookingInfo.daftarWaitinglistDetail.length > 0) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglistDetail.findIndex(p => {
                                                                        if (p.idBooking && idBooking) return p.idBooking.toString().trim() === idBooking.toString().trim();
                                                                        return (p.nama || '').toString().trim() === (nama || '').toString().trim() && (p.level || '').toString().trim() === (level || '').toString().trim();
                                                                    });
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                } else if (Array.isArray(bookingInfo.daftarWaitinglist)) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglist.findIndex(n => (n || '').toString().trim() === (nama || '').toString().trim());
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                }
                                                            } catch (e) {
                                                                posisiLabel = '';
                                                            }
                                                            
                                                            // Debug: log untuk debugging - sama seperti di daftar pemain
                                                            if (isSuperAdmin) {
                                                                console.log('Waitinglist item:', { 
                                                                    nama, 
                                                                    level, 
                                                                    idBooking, 
                                                                    pemainDetail,
                                                                    foundPemainDetail: !!pemainDetail,
                                                                    hasIdBooking: !!idBooking,
                                                                    canClick: isSuperAdmin && !!idBooking,
                                                                    waitinglistDetailLength: bookingInfo.daftarWaitinglistDetail?.length,
                                                                    allWaitinglistDetails: bookingInfo.daftarWaitinglistDetail
                                                                });
                                                            }
                                                            return (
                                                                <Chip
                                                                    key={idx}
                                                                    label={`${posisiLabel}${nama}`}
                                                                    size="small"
                                                                    sx={{
                                                                        // Admin dan superadmin bisa melihat warna hijau (superadmin punya semua akses admin)
                                                                        backgroundColor: theme.palette.warning.light,
                                                                        color: theme.palette.warning.contrastText,
                                                                        fontWeight: 600,
                                                                        // Hanya superadmin yang bisa edit (click untuk edit)
                                                                        cursor: isSuperAdmin && idBooking ? 'pointer' : 'default',
                                                                        '&:hover': isSuperAdmin && idBooking ? {
                                                                            backgroundColor: isPaid ? theme.palette.success.main : theme.palette.warning.main,
                                                                            transform: 'scale(1.05)'
                                                                        } : {}
                                                                    }}
                                                                    onClick={() => {
                                                                        if (isSuperAdmin && idBooking) {
                                                                            console.log('Waitinglist edit clicked:', { idBooking, nama, level, pemainDetail });
                                                                            handleEditPlayer(idBooking);
                                                                        } else {
                                                                            console.log('Waitinglist click blocked:', { isSuperAdmin, idBooking, nama, level });
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                            Belum ada pemain di waitinglist.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            
                            {!gor.bisaBooking && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    GOR ini belum tersedia untuk booking
                                </Alert>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: isMobile ? 2 : 3,
                            background: theme.palette.mode === 'dark' 
                                ? (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)')
                                : undefined,
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(99,170,255,0.22)'
                                : undefined,
                            borderRadius: 3
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography 
                                    variant={isMobile ? "h6" : "h5"} 
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Booking Mabar
                                </Typography>
                                {!user?.email && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size={isMobile ? "small" : "medium"}
                                        startIcon={<LoginIcon />}
                                        onClick={() => {
                                            localStorage.setItem('redirectAfterLogin', window.location.pathname);
                                            navigate('/login');
                                        }}
                                        sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            ml: 2
                                        }}
                                    >
                                        Login
                                    </Button>
                                )}
                            </Box>
                            {!user?.email && (
                                <Alert 
                                    severity="warning" 
                                    sx={{ mb: 2 }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            variant="contained"
                                            startIcon={<LoginIcon />}
                                            onClick={() => {
                                                localStorage.setItem('redirectAfterLogin', window.location.pathname);
                                                navigate('/login');
                                            }}
                                            sx={{ 
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                ml: 1
                                            }}
                                        >
                                            Login
                                        </Button>
                                    }
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        ⚠️ Login Diperlukan
                                    </Typography>
                                    <Typography variant="body2">
                                        Silakan login terlebih dahulu sebelum mengisi form booking. Setelah login, Anda akan kembali ke halaman ini.
                                    </Typography>
                                </Alert>
                            )}
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error === 'Pemain Utama sudah penuh, tidak dapat booking lagi.' ? (
                                        <b>Pemain Utama sudah penuh! Anda tidak dapat booking untuk tanggal ini.</b>
                                    ) : error}
                                </Alert>
                            )}
                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Booking berhasil!
                                </Alert>
                            )}
                            <form onSubmit={handleFormSubmit}>
                                <Grid container spacing={isMobile ? 1 : 2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Tanggal"
                                            value={formData.tanggal}
                                            onChange={(e) => {
                                                setConfirmedBookingPricing(null);
                                                setFormData({ ...formData, tanggal: e.target.value });
                                            }}
                                            required
                                            disabled={!gor.bisaBooking || !user?.email}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            {gor.tanggalTersedia?.map((tanggal) => (
                                                <MenuItem key={tanggal} value={tanggal}>
                                                    {getHariFromTanggal(tanggal)}, {tanggal}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    {pricingPreview && (
                                        <Grid item xs={12}>
                                            <Box sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12), rgba(25, 118, 210, 0.08))',
                                                border: '1px solid',
                                                borderColor: 'warning.light',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.75,
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main', letterSpacing: 0.5 }}>
                                                    ⚡ EARLY BOOKING
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                                        {formatCurrency(displayedPrice ?? pricingPreview.finalPrice)}
                                                    </Typography>
                                                    <Chip
                                                        label={`Diskon ${displayedDiscountPercentage ?? pricingPreview.finalDiscountPercentage}%`}
                                                        color="warning"
                                                        size="small"
                                                        sx={{ fontWeight: 700 }}
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Harga normal {formatCurrency(pricingPreview.normalPrice)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Masih tersedia {Math.max(Math.ceil((pricingPreview.remainingPercentage / 100) * (Number(gor.kapasitasMaksimal || bookingInfo.kapasitasMaksimal || 0))), 0)} dari {Number(gor.kapasitasMaksimal || bookingInfo.kapasitasMaksimal || 0)} slot
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Harga dapat berubah seiring waktu dan jumlah peserta.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nama"
                                            value={formData.nama}
                                            onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                                            margin="normal"
                                            required
                                            disabled={!user?.email}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Level Bermain"
                                            value={formData.levelBermain}
                                            onChange={(e) => setFormData({ ...formData, levelBermain: e.target.value })}
                                            margin="normal"
                                            required
                                            disabled={!user?.email}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            <MenuItem value="Prelevel">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Prelevel</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Baru bermain badminton, kurang paham dengan aturan badminton dan bagaimana cara memukul.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Beginner">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Beginner</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Bisa tepakan, tidak mengerti rotasi, belum bisa mengarahkan bola, kadang sering loss ketika mukul, belum mengerti cara mengeluarkan tenaga, footstep berantakan, tidak bisa backhand.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Intermediate">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Intermediate</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Cukup mengerti rotasi, bisa mengarahkan bola, jarang terjadi loss ketika mukul, mengerti cara mengeluarkan tenaga, tapi footstep masih kurang rapi, backhand kurang lancar.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Advanced">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Advanced</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Mengerti rotasi, bisa mengarahkan bola dan akurat, tidak pernah loss ketika mukul, bisa mengatur pengeluaran tenaga, footstep rapi, backhand lancar dan akurat.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            type="email"
                                            value={formData.email}
                                            disabled
                                            margin="normal"
                                            required
                                            size={isMobile ? "small" : "medium"}
                                            helperText="Email akan terisi otomatis dari akun Google Anda"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                                                },
                                                '& .MuiFormHelperText-root': {
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    marginTop: 0.5
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nomor HP"
                                            type="tel"
                                            value={formData.nomorHP}
                                            onChange={(e) => setFormData({ ...formData, nomorHP: e.target.value })}
                                            margin="normal"
                                            required
                                            disabled={!user?.email}
                                            size={isMobile ? "small" : "medium"}
                                            placeholder="Contoh: 081234567890"
                                            helperText="Nomor HP untuk konfirmasi booking"
                                            inputProps={{
                                                pattern: "[0-9]*",
                                                inputMode: "numeric"
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={useMemberCode}
                                                    onChange={(e) => {
                                                        setUseMemberCode(e.target.checked);
                                                        if (!e.target.checked) {
                                                            setMemberCode('');
                                                            setSelectedMemberInfo(null);
                                                        } else {
                                                            // Auto-fill with first member code if available
                                                            if (memberInfo.length > 0) {
                                                                const firstMember = memberInfo[0];
                                                                setMemberCode(firstMember.memberCode);
                                                                setSelectedMemberInfo(firstMember);
                                                            }
                                                        }
                                                    }}
                                                    disabled={!user?.email || memberInfo.length === 0}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography variant="body2">Bayar dengan Kode Member</Typography>}
                                            sx={{ mt: 1, mb: 1 }}
                                        />
                                        {user?.email && memberInfo.length === 0 && !memberStatusLoading && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 3, display: 'block' }}>
                                                Anda belum memiliki kode member aktif. Hubungi admin untuk info lebih lanjut.
                                            </Typography>
                                        )}
                                    </Grid>
                                    {useMemberCode && (
                                        <>
                                            {memberInfo.length > 1 && (
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        select
                                                        label="Pilih Kode Member"
                                                        value={memberCode}
                                                        onChange={(e) => {
                                                            const selectedCode = e.target.value;
                                                            setMemberCode(selectedCode);
                                                            const selectedMember = memberInfo.find(m => m.memberCode === selectedCode);
                                                            setSelectedMemberInfo(selectedMember || null);
                                                            if (memberCodeError) {
                                                                setMemberCodeError('');
                                                            }
                                                        }}
                                                        margin="normal"
                                                        required
                                                        disabled={!user?.email}
                                                        size={isMobile ? "small" : "medium"}
                                                        helperText="Pilih kode member yang ingin digunakan"
                                                    >
                                                        {memberInfo.map((member, index) => (
                                                            <MenuItem key={`${member.memberCode}-${index}`} value={member.memberCode}>
                                                                {member.memberCode} - {member.status} (Sisa: {member.remainingVisits}, Batas: {member.expiryDate})
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                            )}
                                            {memberInfo.length === 1 && (
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Kode Member"
                                                        value={memberCode}
                                                        onChange={(e) => {
                                                            setMemberCode(e.target.value);
                                                            if (memberCodeError) {
                                                                setMemberCodeError('');
                                                            }
                                                        }}
                                                        margin="normal"
                                                        required
                                                        disabled={!user?.email}
                                                        size={isMobile ? "small" : "medium"}
                                                        placeholder="Masukkan kode member Anda"
                                                        helperText={memberCodeError || 'Kode member wajib diisi saat memilih Bayar dengan Kode Member'}
                                                        error={Boolean(memberCodeError)}
                                                    />
                                                </Grid>
                                            )}
                                            {selectedMemberInfo && (
                                                <Grid item xs={12}>
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2">
                                                                <strong>Sisa Member:</strong> {selectedMemberInfo.remainingVisits} kedatangan
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant="body2">
                                                                    <strong>Batas Tanggal Mabar:</strong> {selectedMemberInfo.expiryDate}
                                                                </Typography>
                                                                <Tooltip 
                                                                    title="Tanggal mabar terakhir bukan aktivitas pembelian booking terakhir"
                                                                    arrow
                                                                >
                                                                    <InfoIcon sx={{ fontSize: '1rem', color: 'info.main', cursor: 'help' }} />
                                                                </Tooltip>
                                                            </Box>
                                                        </Box>
                                                    </Alert>
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={agreeToTerms}
                                                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                                                    disabled={!user?.email}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    Saya setuju dengan{' '}
                                                    <Link
                                                        href="/syarat-ketentuan"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ 
                                                            color: 'primary.main',
                                                            textDecoration: 'none',
                                                            '&:hover': {
                                                                textDecoration: 'underline'
                                                            }
                                                        }}
                                                    >
                                                        Syarat & Ketentuan Mabar RaketerS
                                                    </Link>
                                                </Typography>
                                            }
                                            sx={{ mt: 1, mb: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type={user?.email ? "submit" : "button"}
                                            variant="contained"
                                            color={(bookingInfo.kapasitasMaksimal && bookingInfo.jumlahPemain >= bookingInfo.kapasitasMaksimal) ? "warning" : "primary"}
                                            fullWidth
                                            disabled={loading || !gor.bisaBooking || !agreeToTerms || !user?.email}
                                            size={isMobile ? "large" : "large"}
                                            onClick={!user?.email ? () => {
                                                localStorage.setItem('redirectAfterLogin', window.location.pathname);
                                                navigate('/login');
                                            } : undefined}
                                            sx={{ 
                                                py: isMobile ? 1 : 1.5,
                                                mt: isMobile ? 1 : 2
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} /> : (user?.email ? ((bookingInfo.kapasitasMaksimal && bookingInfo.jumlahPemain >= bookingInfo.kapasitasMaksimal) ? 'JOIN WAITINGLIST' : 'Submit Booking') : 'Login Untuk Booking')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </Grid>
                </Grid>

                <Dialog
                    open={showLoginDialog}
                    onClose={() => setShowLoginDialog(false)}
                    fullScreen={isMobile}
                >
                    <DialogTitle>Login Diperlukan</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Anda perlu login terlebih dahulu untuk melakukan booking. Setelah login, Anda akan kembali ke halaman ini.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowLoginDialog(false)}>Batal</Button>
                        <Button onClick={handleLoginClick} variant="contained" color="primary">
                            Login
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showConfirmationDialog}
                    onClose={handleCloseConfirmation}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                                : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.background.paper} 100%)`,
                            backdropFilter: 'blur(6px)',
                            borderRadius: 4,
                            boxShadow: 8,
                            p: 0
                        }
                    }}
                >
                    <DialogTitle 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-start', 
                            fontWeight: 'bold', 
                            fontSize: { xs: 16, sm: 22 }, 
                            letterSpacing: 1, 
                            pb: 1.5, 
                            borderBottom: '2px solid', 
                            borderColor: 'primary.main', 
                            background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.92)' : 'rgba(255,255,255,0.7)', 
                            color: theme.palette.text.primary, 
                            backdropFilter: 'blur(2px)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 32, borderRadius: 2, background: 'primary.main', mr: 1 }} />
                            Booking Confirmation
                        </Box>
                        <IconButton onClick={handleCloseConfirmation} size="small" sx={{ color: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.700', marginLeft: 'auto' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: { xs: 2, sm: 3 }, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255,255,255,0.85)', borderRadius: 3, mt: 2, color: theme.palette.text.primary }}>
                        <Box sx={{ mb: 2, p: 2, borderRadius: 3, boxShadow: 2, background: theme.palette.mode === 'dark' ? 'rgba(16,28,50,0.95)' : 'rgba(255,255,255,0.95)', color: theme.palette.text.primary }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, letterSpacing: 1 }}>
                                {gor?.nama}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Tanggal:</Typography>
                                    {formData.tanggal === "__MEMBER_1_BULAN__" ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {gor?.tanggalTersedia?.map((tanggal) => (
                                                <Box key={tanggal} sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.primary.light, color: theme.palette.primary.contrastText, fontWeight: 600 }}>
                                                    {getHariFromTanggal(tanggal)}, {tanggal}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.primary.light, color: theme.palette.primary.contrastText, fontWeight: 600 }}>{formData.tanggal}</Box>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Jam Main:</Typography>
                                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.success.light, color: theme.palette.success.contrastText, fontWeight: 600 }}>{gor?.jam}</Box>
                                </Box>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2, borderColor: 'primary.main', opacity: 0.5, borderBottomWidth: 2 }} />
                        
                        {/* Error display dalam dialog konfirmasi */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', letterSpacing: 0.5 }}>
                                Daftar Pemain per Level
                            </Typography>
                            {confirmationLevels.map((level: string) => {
                                const pemainList = confirmationBookingInfo?.pemainByLevel[level] || [];
                                const isUserLevel = level === (localBooking?.level || formData.levelBermain);
                                const userInList = pemainList.includes(localBooking?.nama || formData.nama);
                                const displayList = isUserLevel && !userInList && localBooking?.nama
                                    ? [...pemainList, localBooking.nama]
                                    : pemainList;
                                return (
                                    <Box key={level} sx={{ mb: 2, p: 2, borderRadius: 2, background: theme.palette.mode === 'dark' ? 'rgba(50,60,80,0.7)' : 'rgba(230,245,255,0.7)', boxShadow: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark', mb: 1, letterSpacing: 0.5 }}>
                                            Level {level} <span style={{ fontWeight: 400 }}>({displayList.length} pemain)</span>
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                                            {displayList.map((nama, idx) => {
                                                const isUser = nama === (localBooking?.nama || formData.nama) && isUserLevel;
                                                return (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            background: isUser ? '#fff' : theme.palette.primary.light,
                                                            color: isUser ? theme.palette.primary.main : theme.palette.primary.contrastText,
                                                            px: 2,
                                                            py: 0.7,
                                                            borderRadius: 3,
                                                            fontWeight: isUser ? 800 : 500,
                                                            border: isUser ? `2px solid ${theme.palette.primary.main}` : 'none',
                                                            boxShadow: isUser ? 3 : 0,
                                                            fontSize: '1rem',
                                                            letterSpacing: 0.5
                                                        }}
                                                    >
                                                        {nama}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.75)' : 'rgba(255,255,255,0.7)', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
                        <Button onClick={handleCloseConfirmation} color="inherit" sx={{ fontWeight: 600, borderRadius: 2, px: { xs: 1.5, sm: 3 }, color: theme.palette.text.primary, fontSize: { xs: '0.75rem', sm: '1.1rem' }, minWidth: 0 }}>
                            Batal
                        </Button>
                        <Button onClick={handleConfirmBooking} variant="contained" color="primary" disabled={pendingSubmit} sx={{ fontWeight: 700, borderRadius: 2, px: { xs: 1.5, sm: 3 }, boxShadow: 2, fontSize: { xs: '0.75rem', sm: '1.1rem' }, minWidth: 0 }}>
                            {pendingSubmit ? <CircularProgress size={20} /> : 'Submit Booking'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showCancelDialog}
                    onClose={() => setShowCancelDialog(false)}
                >
                    <DialogTitle>Batalkan Pemesanan?</DialogTitle>
                    <DialogContent>
                        <Typography>Anda yakin ingin membatalkan pemesanan booking ini?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowCancelDialog(false)} color="inherit">Tidak</Button>
                        <Button onClick={handleCancelBooking} color="error">Ya, Batalkan</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showCalendarDialog}
                    onClose={handleSkipCalendar}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
                        Tambahkan Event ke Calendar Anda?
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center', pb: 0 }}>
                        <Typography sx={{ mb: 2 }}>
                            Agar tidak lupa jadwal main, tambahkan event booking ini ke kalender Anda!
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button onClick={handleAddToCalendar} variant="contained" color="primary" sx={{ fontWeight: 700, px: 3 }}>
                            Tambahkan ke Kalender
                        </Button>
                        <Button onClick={handleSkipCalendar} color="inherit" sx={{ fontWeight: 600, px: 3 }}>
                            Lewati
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={showBlockedLevelDialog} onClose={() => setShowBlockedLevelDialog(false)}>
                    <DialogTitle>Peringatan</DialogTitle>
                    <DialogContent>
                        <Typography>{blockedLevelMsg}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowBlockedLevelDialog(false)} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={showKapasitasDialog} onClose={() => setShowKapasitasDialog(false)}>
                    <DialogTitle>Pemain Utama Penuh</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {error}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowKapasitasDialog(false)} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={!!editDialog?.open}
                    onClose={() => setEditDialog(null)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                                : undefined,
                            borderRadius: 3,
                        }
                    }}
                >
                    <DialogTitle sx={{ background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.92)' : undefined, color: theme.palette.text.primary, borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : undefined }}>
                        Edit Booking
                    </DialogTitle>
                    <DialogContent sx={{ background: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined, color: theme.palette.text.primary }}>
                        <TextField label="Nama" fullWidth value={editField.nama || ''} onChange={e => setEditField(f => ({...f, nama: e.target.value}))} sx={{ mb: 2 }} />
                        <Select label="Level" value={editField.level || ''} fullWidth onChange={e => setEditField(f => ({...f, level: e.target.value}))}>
                            {(editDialog?.levelList||[]).map(lvl =>
                                <MenuItem value={lvl} key={lvl}>{lvl}</MenuItem>
                            )}
                        </Select>
                        <Box my={2} display="flex" alignItems="center" gap={2}>
                            <Typography>Status Bayar:</Typography>
                            <Select
                                fullWidth
                                value={editField.statusBayar || 'Belum bayar'}
                                onChange={e => setEditField(f => ({ ...f, statusBayar: e.target.value }))}
                            >
                                <MenuItem value="Sudah bayar">Sudah bayar</MenuItem>
                                <MenuItem value="Belum bayar">Belum bayar</MenuItem>
                                <MenuItem value="Bayar di lapang">Bayar di lapang</MenuItem>
                            </Select>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.75)' : undefined }}>
                        <Button onClick={()=>setEditDialog(null)}>Batal</Button>
                        <Button color="error" onClick={handleEditDelete} disabled={editLoading}>Hapus</Button>
                        <Button onClick={handleEditSubmit} variant="contained" disabled={editLoading}>{editLoading? 'Menyimpan...' : 'Simpan'}</Button>
                    </DialogActions>
                </Dialog>

                {/* Payment Loading Dialog */}
                <Dialog open={showPaymentLoadingDialog} disableEscapeKeyDown PaperProps={{ sx: { background: theme.palette.mode === 'dark' ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)` : undefined, borderRadius: 3 } }}>
                    <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                        Melanjutkan ke Pembayaran...
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center', py: 3, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined }}>
                        <CircularProgress size={50} sx={{ mb: 2 }} />
                        <Typography variant="body1">
                            Mohon tunggu sebentar, sedang memproses pembayaran...
                        </Typography>
                    </DialogContent>
                </Dialog>

                {/* Share Dialog */}
                <Dialog
                    open={showShareDialog}
                    onClose={() => setShowShareDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            backgroundColor: 'background.paper'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1
                    }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                            📤 Bagikan Info Booking
                        </Typography>
                        <IconButton onClick={() => setShowShareDialog(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Pilih platform untuk membagikan info booking terkini:
                        </Typography>

                        <List sx={{ pt: 0 }}>
                            <ListItem
                                button
                                onClick={shareToWhatsApp}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="WhatsApp"
                                    secondary="Bagikan ke WhatsApp"
                                />
                            </ListItem>

                            <ListItem
                                button
                                onClick={shareToTwitter}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <TwitterIcon sx={{ color: '#1DA1F2', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Twitter"
                                    secondary="Tweet info booking"
                                />
                            </ListItem>

                            <ListItem
                                button
                                onClick={shareToFacebook}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <FacebookIcon sx={{ color: '#1877F2', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Facebook"
                                    secondary="Post ke Facebook"
                                />
                            </ListItem>

                            <ListItem
                                button
                                onClick={shareToTelegram}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <TelegramIcon sx={{ color: '#0088cc', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Telegram"
                                    secondary="Bagikan ke Telegram"
                                />
                            </ListItem>

                            <ListItem
                                button
                                onClick={copyToClipboard}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <ContentCopyIcon sx={{ fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Salin ke Clipboard"
                                    secondary="Salin teks untuk dibagikan"
                                />
                            </ListItem>
                        </List>
                    </DialogContent>
                </Dialog>

                <Snackbar
                    open={shareSnackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setShareSnackbarOpen(false)}
                    message="Info booking berhasil disalin ke clipboard!"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    ContentProps={{
                        sx: {
                            backgroundColor: '#4caf50',
                            color: '#fff',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 500
                        }
                    }}
                />

                <Dialog
                    open={showPaymentErrorDialog}
                    onClose={() => setShowPaymentErrorDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ textAlign: 'center', color: 'error.main' }}>
                        ⚠️ Gagal Memproses Pembayaran
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                            Sistem pembayaran mengalami gangguan sementara.
                            <br />
                            Silakan coba lagi atau hubungi admin jika masalah berlanjut.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleRetryPayment}
                                startIcon={<RefreshIcon />}
                            >
                                Coba Lagi
                            </Button>
                            <Button
                                variant="outlined"
                                color="success"
                                href="https://wa.me/+6283890485939"
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<WhatsAppIcon />}
                            >
                                Hubungi Admin
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Container>
        </>
    );
};

export default GorDetail; 
